import axios from 'axios';
import { getTokens, setTokens, removeTokens } from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000, // 15 second timeout
  withCredentials: true, // Ensure cookies/credentials are sent
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const { access } = getTokens();
    if (access) {
      config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Requests where a 401 means "wrong credentials/link", not "session expired"
const NO_REFRESH_PATHS = ['/auth/login/', '/auth/register/', '/auth/password/reset/', '/auth/verify-email/', '/auth/google/'];

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthRequest = NO_REFRESH_PATHS.some((p) => originalRequest?.url?.startsWith(p));

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;

      try {
        const { refresh } = getTokens();
        if (refresh) {
          const response = await axios.post(`${API_BASE_URL}/api/auth/token/refresh/`, {
            refresh,
          });
          
          const { access } = response.data;
          setTokens(access, refresh);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        removeTokens();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

const authAPI = {
  // Authentication
  login: async (email, password) => {
    try {
      const response = await api.post('/auth/login/', { email, password });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  register: async (userData) => {
    try {
      const response = await api.post('/auth/register/', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  logout: async (refreshToken) => {
    const response = await api.post('/auth/logout/', { refresh_token: refreshToken });
    return response.data;
  },

  refreshToken: async (refreshToken) => {
    const response = await api.post('/auth/token/refresh/', { refresh: refreshToken });
    return response.data;
  },

  // Profile
  getProfile: async () => {
    const response = await api.get('/auth/profile/');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/user/');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.patch('/auth/profile/', profileData);
    return response.data;
  },

  // Password management
  changePassword: async (passwordData) => {
    const response = await api.post('/auth/password/change/', passwordData);
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/password/reset/', { email });
    return response.data;
  },

  confirmPasswordReset: async (uid, token, newPassword, newPasswordConfirm) => {
    const response = await api.post('/auth/password/reset/confirm/', {
      uid,
      token,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm,
    });
    return response.data;
  },

  // Email verification
  verifyEmail: async (uid, token) => {
    const response = await api.post(`/auth/verify-email/${uid}/${token}/`);
    return response.data;
  },

  resendVerificationEmail: async (email) => {
    const response = await api.post('/auth/resend-verification/', { email });
    return response.data;
  },

  // Google OAuth
  getGoogleAuthUrl: async (redirectUri) => {
    const params = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '';
    const response = await api.get(`/auth/google/url/${params}`);
    return response.data;
  },

  googleOneTap: async (credential) => {
    const response = await api.post('/auth/google/one-tap/', { credential });
    return response.data;
  },

  googleLogin: async (code, redirectUri) => {
    const response = await api.post('/auth/google/login/', {
      code,
      redirect_uri: redirectUri
    });
    return response.data;
  },
};

export default authAPI;
